import { DashboardBucketDto } from '../../dto/report';

type DashboardBucketPlacement = 'operational' | 'side';
type DashboardBucketTarget = 'orders' | 'transports';
type DashboardBucketFilter = 'upcomingDeadlines';

export interface DashboardBucketViewModel extends DashboardBucketDto {
  labelKey: string;
  tone: string;
  icon: string;
  target: DashboardBucketTarget;
  filter?: DashboardBucketFilter;
}

export interface DashboardStatusCardViewModel {
  key: string;
  labelKey: string;
  value: number;
  href: string;
  tone: string;
}

const OPERATIONAL_BUCKETS: Record<string, Omit<DashboardBucketViewModel, keyof DashboardBucketDto>> = {
  attention: {
    labelKey: 'dashboard:workQueue.cards.attention',
    tone: 'danger',
    icon: 'alert',
    target: 'orders',
  },
  dispatch: {
    labelKey: 'dashboard:workQueue.cards.dispatch',
    tone: 'success',
    icon: 'fileDone',
    target: 'orders',
  },
  transport: {
    labelKey: 'dashboard:workQueue.cards.transport',
    tone: 'warning',
    icon: 'truck',
    target: 'orders',
  },
};

const SIDE_BUCKETS: Record<string, Omit<DashboardBucketViewModel, keyof DashboardBucketDto>> = {
  production: {
    labelKey: 'dashboard:sidePanel.production',
    tone: 'neutral',
    icon: 'clock',
    target: 'orders',
  },
  transport: {
    labelKey: 'dashboard:sidePanel.transport',
    tone: 'neutral',
    icon: 'truck',
    target: 'orders',
  },
};

const getBucketPresentation = (bucket: DashboardBucketDto, placement: DashboardBucketPlacement) => {
  const presentation = placement === 'operational'
    ? OPERATIONAL_BUCKETS[bucket.key]
    : SIDE_BUCKETS[bucket.key];

  return presentation || {
    labelKey: bucket.key,
    tone: 'neutral',
    icon: 'alert',
    target: 'orders' as const,
  };
};

export const buildDashboardBucketViewModels = (
  buckets: DashboardBucketDto[],
  placement: DashboardBucketPlacement,
): DashboardBucketViewModel[] => buckets.map((bucket) => ({
  ...bucket,
  ...getBucketPresentation(bucket, placement),
}));

export const buildDashboardDeadlineBucket = (count: number): DashboardBucketViewModel => ({
  key: 'deadlines',
  statuses: [],
  count,
  labelKey: 'dashboard:workQueue.cards.deadlines',
  tone: 'neutral',
  icon: 'calendar',
  target: 'orders',
  filter: 'upcomingDeadlines',
});

const countStatuses = (statusCounts: Record<string, number>, statuses: string[]) =>
  statuses.reduce((sum, status) => sum + (statusCounts[status] || 0), 0);

const getBucket = (buckets: DashboardBucketViewModel[], key: string) =>
  buckets.find((bucket) => bucket.key === key);

export const buildDashboardStatusCards = (
  statusCounts: Record<string, number>,
  operationalBuckets: DashboardBucketViewModel[],
  sideBuckets: DashboardBucketViewModel[],
  overdueCount: number,
): DashboardStatusCardViewModel[] => {
  const attention = getBucket(operationalBuckets, 'attention');
  const dispatch = getBucket(operationalBuckets, 'dispatch');
  const production = getBucket(sideBuckets, 'production');
  const transport = getBucket(operationalBuckets, 'transport');

  return [
    {
      key: 'attention',
      labelKey: 'dashboard:statusCards.inquiries',
      value: attention?.count || 0,
      href: attention ? buildDashboardBucketLink(attention) : '/app/orders',
      tone: 'slate',
    },
    {
      key: 'confirmed',
      labelKey: 'dashboard:statusCards.confirmed',
      value: countStatuses(statusCounts, ['CONFIRMED']),
      href: '/app/orders?status=CONFIRMED',
      tone: 'blue',
    },
    {
      key: 'production',
      labelKey: 'dashboard:statusCards.production',
      value: production?.count || 0,
      href: production ? buildDashboardBucketLink(production) : '/app/orders',
      tone: 'sky',
    },
    {
      key: 'transport',
      labelKey: 'dashboard:statusCards.transport',
      value: transport?.count || 0,
      href: transport ? buildDashboardBucketLink(transport) : '/app/orders',
      tone: 'orange',
    },
    {
      key: 'documents',
      labelKey: 'dashboard:statusCards.documents',
      value: dispatch?.count || 0,
      href: dispatch ? buildDashboardBucketLink(dispatch) : '/app/orders',
      tone: 'violet',
    },
    {
      key: 'overdue',
      labelKey: 'dashboard:statusCards.overdue',
      value: overdueCount,
      href: '/app/orders?deadline=overdue',
      tone: 'red',
    },
  ];
};

export const buildDashboardBucketLink = (bucket: Pick<DashboardBucketViewModel, 'target' | 'statuses' | 'filter'>) => {
  if (bucket.target === 'transports') {
    return '/app/transports';
  }

  if (bucket.filter === 'upcomingDeadlines') {
    return '/app/orders?deadline=upcoming';
  }

  if (!bucket.statuses.length) {
    return '/app/orders';
  }

  return `/app/orders?status=${bucket.statuses.join(',')}`;
};
